import React, { useState } from 'react';
import { useCRM } from '../../utils/CRMContext';
import { Pipeline, PipelineStage } from '../../types';
import './PipelineManager.css';

interface PipelineManagerProps {
  onClose: () => void;
}

const PRESET_COLORS = [
  '#2C3E2F', // Dark green
  '#4F423A', // Deep taupe
  '#4299E1', // Blue
  '#48BB78', // Green
  '#ED8936', // Orange
  '#E53E3E', // Red
  '#9F7AEA', // Purple
  '#38B2AC', // Teal
];

const generateStageId = (name: string) => 
  name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

export const PipelineManager: React.FC<PipelineManagerProps> = ({ onClose }) => {
  const { pipelines, createPipeline, updatePipeline, deletePipeline } = useCRM();
  const [expandedPipelineId, setExpandedPipelineId] = useState<string | null>(null);
  const [editingPipeline, setEditingPipeline] = useState<Pipeline | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New pipeline form state
  const [newPipelineName, setNewPipelineName] = useState('');
  const [newPipelineColor, setNewPipelineColor] = useState(PRESET_COLORS[0]);
  const [newPipelineStages, setNewPipelineStages] = useState<PipelineStage[]>([
    { id: 'qualified', name: 'Qualified', order: 1, probability: 20, rottingDays: 7 },
    { id: 'proposal', name: 'Proposal', order: 2, probability: 40, rottingDays: 14 },
    { id: 'negotiation', name: 'Negotiation', order: 3, probability: 60, rottingDays: 21 },
    { id: 'closing', name: 'Closing', order: 4, probability: 80, rottingDays: 14 },
  ]);

  const resetNewPipelineForm = () => {
    setNewPipelineName('');
    setNewPipelineColor(PRESET_COLORS[0]);
    setNewPipelineStages([
      { id: 'qualified', name: 'Qualified', order: 1, probability: 20, rottingDays: 7 },
      { id: 'proposal', name: 'Proposal', order: 2, probability: 40, rottingDays: 14 },
      { id: 'negotiation', name: 'Negotiation', order: 3, probability: 60, rottingDays: 21 },
      { id: 'closing', name: 'Closing', order: 4, probability: 80, rottingDays: 14 },
    ]);
  };

  const handleCreatePipeline = async () => {
    if (!newPipelineName.trim()) {
      setError('Pipeline name is required');
      return;
    }
    if (newPipelineStages.length === 0) {
      setError('Pipeline must have at least one stage');
      return;
    }

    try {
      await createPipeline({
        name: newPipelineName.trim(),
        color: newPipelineColor,
        stages: newPipelineStages,
        isDefault: pipelines.length === 0,
      });
      resetNewPipelineForm();
      setIsCreating(false);
      setError(null);
    } catch (err) {
      console.error('Error creating pipeline:', err);
      setError('Failed to create pipeline');
    }
  };

  const handleUpdatePipeline = async (pipeline: Pipeline) => {
    if (!pipeline.id) return;
    try {
      await updatePipeline(pipeline.id, {
        name: pipeline.name,
        color: pipeline.color,
        stages: pipeline.stages,
        isDefault: pipeline.isDefault,
      });
      setEditingPipeline(null);
      setError(null);
    } catch (err) {
      console.error('Error updating pipeline:', err);
      setError('Failed to update pipeline');
    }
  };

  const handleDeletePipeline = async (pipelineId: string) => {
    if (!window.confirm('Delete this pipeline? Deals in this pipeline will need to be reassigned.')) {
      return;
    }
    try {
      await deletePipeline(pipelineId);
      if (expandedPipelineId === pipelineId) {
        setExpandedPipelineId(null);
      }
    } catch (err) {
      console.error('Error deleting pipeline:', err);
      setError('Failed to delete pipeline');
    }
  };

  const handleSetDefault = async (pipelineId: string) => {
    try {
      // Unset current default
      const currentDefault = pipelines.find(p => p.isDefault);
      if (currentDefault?.id && currentDefault.id !== pipelineId) {
        await updatePipeline(currentDefault.id, { isDefault: false });
      }
      // Set new default
      await updatePipeline(pipelineId, { isDefault: true });
    } catch (err) {
      console.error('Error setting default pipeline:', err);
      setError('Failed to set default pipeline');
    }
  };

  const handleClonePipeline = async (pipeline: Pipeline) => {
    try {
      await createPipeline({
        name: `${pipeline.name} (Copy)`,
        color: pipeline.color,
        stages: pipeline.stages.map(s => ({ ...s })),
        isDefault: false,
      });
    } catch (err) {
      console.error('Error cloning pipeline:', err);
      setError('Failed to clone pipeline');
    }
  };

  // Stage management helpers
  const addStageToNew = () => {
    const newOrder = newPipelineStages.length + 1;
    setNewPipelineStages([
      ...newPipelineStages,
      {
        id: `stage-${newOrder}`,
        name: `Stage ${newOrder}`,
        order: newOrder,
        probability: Math.min(90, newOrder * 20),
        rottingDays: 14,
      },
    ]);
  };

  const updateNewStage = (index: number, updates: Partial<PipelineStage>) => {
    const updated = [...newPipelineStages];
    updated[index] = { ...updated[index], ...updates };
    // Don't update ID during editing to prevent input losing focus
    setNewPipelineStages(updated);
  };

  const removeNewStage = (index: number) => {
    const updated = newPipelineStages.filter((_, i) => i !== index);
    // Reorder remaining stages
    updated.forEach((stage, i) => {
      stage.order = i + 1;
    });
    setNewPipelineStages(updated);
  };

  const moveNewStage = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === newPipelineStages.length - 1) return;

    const updated = [...newPipelineStages];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
    updated.forEach((stage, i) => {
      stage.order = i + 1;
    });
    setNewPipelineStages(updated);
  };

  // Editing pipeline stage management
  const addStageToEditing = () => {
    if (!editingPipeline) return;
    const newOrder = editingPipeline.stages.length + 1;
    setEditingPipeline({
      ...editingPipeline,
      stages: [
        ...editingPipeline.stages,
        {
          id: `stage-${newOrder}-${Date.now()}`,
          name: `Stage ${newOrder}`,
          order: newOrder,
          probability: Math.min(90, newOrder * 20),
          rottingDays: 14,
        },
      ],
    });
  };

  const updateEditingStage = (index: number, updates: Partial<PipelineStage>) => {
    if (!editingPipeline) return;
    const updated = [...editingPipeline.stages];
    updated[index] = { ...updated[index], ...updates };
    // Don't update ID during editing to prevent input losing focus
    setEditingPipeline({ ...editingPipeline, stages: updated });
  };

  const removeEditingStage = (index: number) => {
    if (!editingPipeline) return;
    const updated = editingPipeline.stages.filter((_, i) => i !== index);
    updated.forEach((stage, i) => {
      stage.order = i + 1;
    });
    setEditingPipeline({ ...editingPipeline, stages: updated });
  };

  const moveEditingStage = (index: number, direction: 'up' | 'down') => {
    if (!editingPipeline) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === editingPipeline.stages.length - 1) return;

    const updated = [...editingPipeline.stages];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
    updated.forEach((stage, i) => {
      stage.order = i + 1;
    });
    setEditingPipeline({ ...editingPipeline, stages: updated });
  };

  const renderStageEditor = (
    stages: PipelineStage[],
    updateStage: (index: number, updates: Partial<PipelineStage>) => void,
    removeStage: (index: number) => void,
    moveStage: (index: number, direction: 'up' | 'down') => void,
    addStage: () => void
  ) => (
    <div className="stages-editor">
      <div className="stages-list">
        {stages.sort((a, b) => a.order - b.order).map((stage, index) => (
          <div key={`stage-${index}`} className="stage-item">
            <div className="stage-drag">≡</div>
            <div className="stage-fields">
              <input
                type="text"
                value={stage.name}
                onChange={(e) => updateStage(index, { name: e.target.value })}
                placeholder="Stage name"
                className="stage-name-input"
              />
              <div className="stage-meta-inputs">
                <div className="meta-input">
                  <label>Prob:</label>
                  <input
                    type="number"
                    value={stage.probability}
                    onChange={(e) => updateStage(index, { probability: parseInt(e.target.value) || 0 })}
                    min="0"
                    max="100"
                  />
                  <span>%</span>
                </div>
                <div className="meta-input">
                  <label>Rotting:</label>
                  <input
                    type="number"
                    value={stage.rottingDays || ''}
                    onChange={(e) => updateStage(index, { rottingDays: parseInt(e.target.value) || undefined })}
                    min="1"
                  />
                  <span>days</span>
                </div>
              </div>
            </div>
            <div className="stage-actions">
              <button 
                className="move-btn" 
                onClick={() => moveStage(index, 'up')}
                disabled={index === 0}
              >
                ↑
              </button>
              <button 
                className="move-btn" 
                onClick={() => moveStage(index, 'down')}
                disabled={index === stages.length - 1}
              >
                ↓
              </button>
              <button 
                className="remove-btn"
                onClick={() => removeStage(index)}
                disabled={stages.length <= 1}
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
      <button className="add-stage-btn" onClick={addStage}>
        + Add Stage
      </button>
    </div>
  );

  return (
    <div className="pipeline-manager-overlay" onClick={onClose}>
      <div className="pipeline-manager" onClick={e => e.stopPropagation()}>
        <div className="manager-header">
          <h2>Manage Pipelines</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <div className="pipelines-list">
          {pipelines.map(pipeline => (
            <div 
              key={pipeline.id} 
              className={`pipeline-item ${expandedPipelineId === pipeline.id ? 'expanded' : ''}`}
            >
              <div 
                className="pipeline-header"
                onClick={() => setExpandedPipelineId(
                  expandedPipelineId === pipeline.id ? null : pipeline.id!
                )}
              >
                <div 
                  className="pipeline-color-bar"
                  style={{ backgroundColor: pipeline.color }}
                />
                <div className="pipeline-info">
                  <span className="pipeline-name">
                    {pipeline.name}
                    {pipeline.isDefault && (
                      <span className="default-badge">Default</span>
                    )}
                  </span>
                  <span className="pipeline-stages-summary">
                    {pipeline.stages.sort((a, b) => a.order - b.order).map(s => s.name).join(' → ')}
                  </span>
                </div>
                <span className="expand-icon">
                  {expandedPipelineId === pipeline.id ? '▼' : '▶'}
                </span>
              </div>

              {expandedPipelineId === pipeline.id && (
                <div className="pipeline-details">
                  {editingPipeline?.id === pipeline.id ? (
                    <div className="editing-section">
                      <div className="form-row">
                        <label>Pipeline Name</label>
                        <input
                          type="text"
                          value={editingPipeline.name}
                          onChange={(e) => setEditingPipeline({
                            ...editingPipeline,
                            name: e.target.value
                          })}
                        />
                      </div>

                      <div className="form-row">
                        <label>Color</label>
                        <div className="color-picker">
                          {PRESET_COLORS.map(color => (
                            <button
                              key={color}
                              type="button"
                              className={`color-option ${editingPipeline.color === color ? 'selected' : ''}`}
                              style={{ backgroundColor: color }}
                              onClick={() => setEditingPipeline({
                                ...editingPipeline,
                                color
                              })}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="form-row">
                        <label>Stages</label>
                        {renderStageEditor(
                          editingPipeline.stages,
                          updateEditingStage,
                          removeEditingStage,
                          moveEditingStage,
                          addStageToEditing
                        )}
                      </div>

                      <div className="editing-actions">
                        <button 
                          className="cancel-btn"
                          onClick={() => setEditingPipeline(null)}
                        >
                          Cancel
                        </button>
                        <button 
                          className="save-btn"
                          onClick={() => handleUpdatePipeline(editingPipeline)}
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="stages-preview">
                        {pipeline.stages.sort((a, b) => a.order - b.order).map(stage => (
                          <div key={stage.id} className="stage-preview-item">
                            <span className="stage-preview-name">{stage.name}</span>
                            <span className="stage-preview-meta">
                              {stage.probability}% • {stage.rottingDays || '∞'} days
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="pipeline-actions">
                        <button 
                          className="action-btn edit"
                          onClick={() => setEditingPipeline(pipeline)}
                        >
                          Edit
                        </button>
                        <button 
                          className="action-btn clone"
                          onClick={() => handleClonePipeline(pipeline)}
                        >
                          Clone
                        </button>
                        {!pipeline.isDefault && (
                          <button 
                            className="action-btn default"
                            onClick={() => pipeline.id && handleSetDefault(pipeline.id)}
                          >
                            Set Default
                          </button>
                        )}
                        {pipelines.length > 1 && (
                          <button 
                            className="action-btn delete"
                            onClick={() => pipeline.id && handleDeletePipeline(pipeline.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Create New Pipeline */}
        {isCreating ? (
          <div className="create-pipeline-form">
            <h3>Create New Pipeline</h3>
            
            <div className="form-row">
              <label>Pipeline Name</label>
              <input
                type="text"
                value={newPipelineName}
                onChange={(e) => setNewPipelineName(e.target.value)}
                placeholder="e.g., Timber Frame Kits"
              />
            </div>

            <div className="form-row">
              <label>Color</label>
              <div className="color-picker">
                {PRESET_COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    className={`color-option ${newPipelineColor === color ? 'selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewPipelineColor(color)}
                  />
                ))}
              </div>
            </div>

            <div className="form-row">
              <label>Stages</label>
              {renderStageEditor(
                newPipelineStages,
                updateNewStage,
                removeNewStage,
                moveNewStage,
                addStageToNew
              )}
            </div>

            <div className="form-actions">
              <button 
                className="cancel-btn"
                onClick={() => { setIsCreating(false); resetNewPipelineForm(); }}
              >
                Cancel
              </button>
              <button 
                className="create-btn"
                onClick={handleCreatePipeline}
              >
                Create Pipeline
              </button>
            </div>
          </div>
        ) : (
          <button 
            className="new-pipeline-btn"
            onClick={() => setIsCreating(true)}
          >
            + Create New Pipeline
          </button>
        )}
      </div>
    </div>
  );
};

export default PipelineManager;
